import { useEffect, useState } from 'react';

import viewCampaigns from '@/shared/api/campaigns/viewCampaigns';
import getCustomers from '@/shared/api/customers/getCustomers';
import viewShippingProfiles from '@/shared/api/shipping/profiles/viewShippingProfiles';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import Campaign from '@/shared/types/campaigns/campaign.type';
import Customer from '@/shared/types/customer.type';
import ShippingProfile from '@/shared/types/shipping/shippingProfile.type';

type Args = { checkoutCurrency: string };

const useDefaultSettings = ({ checkoutCurrency }: Args) => {
  const redirectLogin = useRedirectLogin();

  const [defaultSettings, setDefaultSettings] = useState({
    in_campaign_id: '',
    in_offer_id: '',
    in_customer_id: '',
    shipping_profile_id: '',
    shipping_profile_zone_id: '',
    shipping_price: '',
    // Todo: replace with actual value
    currency: 'CAD',
    // Todo: Check does it still be commented?
    // processor_id: '',
  });

  const [defaultCustomer, setDefaultCustomer] = useState<Customer | null>(null);

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setDefaultSettings((prev) => ({ ...prev, currency: checkoutCurrency }));
  }, [checkoutCurrency]);

  useEffect(() => {
    const fetchPOSDefaults = async () => {
      const campaignPromise = await viewCampaigns({
        body: {
          search: [
            {
              campaign_name: {
                cop: ['ilike'],
                value: 'POS-default',
              },
            },
          ],
        },
        onUnauthorized: redirectLogin,
      });

      const customerPromise = await getCustomers(
        {
          search: [
            {
              first_name: {
                cop: ['ilike'],
                value: 'POS',
              },
            },
          ],
        },
        redirectLogin,
      );

      const shippingProfilePromise = await viewShippingProfiles({
        body: {
          search: [
            {
              display_name: {
                cop: ['ilike'],
                value: 'POS',
              },
            },
          ],
        },
        onUnauthorized: redirectLogin,
      });

      const promises = [
        campaignPromise,
        customerPromise,
        shippingProfilePromise,
      ];

      await Promise.allSettled(promises)
        .then((results) => {
          const [campaign, customer, sProfile] = results;

          if (campaign.status === 'fulfilled') {
            const response = campaign.value.data as Campaign[];

            if (response.length > 0) {
              setDefaultSettings((prev) => ({
                ...prev,
                in_campaign_id: response[0].in_campaign_id,
                in_offer_id: response[0].in_offer_id,
              }));
            }
          }

          if (customer.status === 'fulfilled') {
            const response = customer.value.data as Customer[];

            if (response.length > 0) {
              setDefaultSettings((prev) => ({
                ...prev,
                in_customer_id: response[0].in_customer_id,
              }));
              setDefaultCustomer(response[0]);
            }
          }

          if (sProfile.status === 'fulfilled') {
            const response = sProfile.value.data as ShippingProfile[];

            if (response.length > 0) {
              const { zones } = response[0];

              setDefaultSettings((prev) => ({
                ...prev,
                shipping_profile_id: zones[0].shipping_profile_id,
                shipping_profile_zone_id: zones[0].shipping_profile_zone_id,
                shipping_price: zones[0].shipping_rate,
              }));
            }
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    };

    void fetchPOSDefaults();
  }, [redirectLogin]);

  return { defaultSettings, defaultCustomer, isLoading };
};

export default useDefaultSettings;
